import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { AppLayout } from "@/components/layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { StudentBarcodes } from "@/components/administration/StudentBarcodes";
import { useQrVisibility } from "@/hooks/useQrVisibility";
import {
  User, GraduationCap, Calendar, MapPin, DollarSign, ClipboardCheck, QrCode,
  ArrowLeft, Fingerprint, Clock, ArrowRight, Eye, EyeOff, Mail,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ChangeStudentEmailDialog } from "@/components/user-management/ChangeStudentEmailDialog";
import { useQueryClient } from "@tanstack/react-query";

function formatStatus(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

function getProgram(cls: string | null): string {
  if (!cls) return "—";
  const prefix = cls.toUpperCase();
  if (prefix.startsWith("CMT")) return "Clinical Medicine";
  if (prefix.startsWith("PST")) return "Pharmaceutical Sciences";
  if (prefix.startsWith("SW")) return "Social Work";
  return "—";
}

export default function StudentFile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { checkVisibility } = useQrVisibility();
  const { roles } = useAuth();
  const queryClient = useQueryClient();
  const canChangeEmail = roles.includes("superadmin") || roles.includes("head_administration");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [fingerprintUrl, setFingerprintUrl] = useState<string | null>(null);

  const { data: student, isLoading } = useQuery({
    queryKey: ["student-file", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("students").select("*").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: biometrics } = useQuery({
    queryKey: ["student-biometrics", id],
    queryFn: async () => {
      const { data } = await supabase.from("student_biometrics").select("*").eq("student_id", id!).maybeSingle();
      return data;
    },
    enabled: !!id,
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ["student-registrations-file", id],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("id, semester, academic_year, status, courses(course_code, course_name)").eq("student_id", id!).order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!id,
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["student-invoices-file", id],
    queryFn: async () => {
      const { data } = await supabase.from("invoices").select("*").eq("student_id", id!).order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!id,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["student-payments-file", id],
    queryFn: async () => {
      const { data } = await supabase.from("payments").select("*").eq("student_id", id!).order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!id,
  });

  const { data: examResults = [] } = useQuery({
    queryKey: ["student-results-file", id],
    queryFn: async () => {
      const { data } = await supabase.from("exam_results").select("*, courses(course_code, course_name)").eq("student_id", id!).order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!id,
  });

  const { data: statusHistory = [] } = useQuery({
    queryKey: ["student-status-history-file", id],
    queryFn: async () => {
      const { data } = await supabase.from("student_status_history").select("*").eq("student_id", id!).order("created_at", { ascending: true });
      return data ?? [];
    },
    enabled: !!id,
  });

  // Load biometric URLs
  useEffect(() => {
    if (!biometrics) return;
    (async () => {
      const [photo, sig, fp] = await Promise.all([
        biometrics.photo_path ? getSignedFileUrl(biometrics.photo_path) : null,
        biometrics.signature_path ? getSignedFileUrl(biometrics.signature_path) : null,
        biometrics.fingerprint_path ? getSignedFileUrl(biometrics.fingerprint_path) : null,
      ]);
      setPhotoUrl(photo);
      setSignatureUrl(sig);
      setFingerprintUrl(fp);
    })();
  }, [biometrics]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Loading student file...</p>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          <Card><CardContent className="py-8 text-center text-muted-foreground">Student not found.</CardContent></Card>
        </div>
      </AppLayout>
    );
  }

  const totalPaid = payments.reduce((s, p) => s + Number(p.amount), 0);
  const totalInvoiced = invoices.reduce((s, i) => s + Number(i.amount), 0);
  const outstanding = totalInvoiced - totalPaid;

  const qrVisAdmin = checkVisibility("admin");
  const qrVisAcademic = checkVisibility("academic");
  const qrVisExam = checkVisibility("examination");
  const qrVisAccounting = checkVisibility("accounting");

  return (
    <AppLayout>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg sm:text-xl font-bold">Student File</h1>
          {canChangeEmail && student?.user_id && (
            <Button variant="outline" size="sm" className="ml-auto gap-1" onClick={() => setEmailDialogOpen(true)}>
              <Mail className="h-3.5 w-3.5" /> Change Email
            </Button>
          )}
        </div>

        {canChangeEmail && student?.user_id && (
          <ChangeStudentEmailDialog
            open={emailDialogOpen}
            onOpenChange={setEmailDialogOpen}
            userId={student.user_id}
            studentName={`${student.first_name} ${student.last_name}`}
            currentEmail={student.email ?? null}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ["student-file", id] })}
          />
        )}

        {/* Header Card */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4 items-start">
              {photoUrl ? (
                <img src={photoUrl} alt="Student" className="w-[105px] h-[135px] rounded-md object-cover border" />
              ) : (
                <div className="w-[105px] h-[135px] rounded-md border bg-muted flex items-center justify-center text-muted-foreground">
                  <User className="h-8 w-8" />
                </div>
              )}
              <div className="space-y-1.5 flex-1">
                <h2 className="text-xl font-bold">{student.first_name} {student.last_name}</h2>
                <p className="text-sm text-muted-foreground font-mono">{student.student_number}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{formatStatus(student.status)}</Badge>
                  {student.sit_status && <Badge variant="secondary">{formatStatus(student.sit_status)}</Badge>}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                  <div><span className="text-muted-foreground">Class:</span> {student.class || "—"}</div>
                  <div><span className="text-muted-foreground">Program:</span> {getProgram(student.class)}</div>
                  <div><span className="text-muted-foreground">NTA Level:</span> {student.nta_level || "—"}</div>
                  <div><span className="text-muted-foreground">Semester:</span> {student.current_semester || "—"}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed Sections */}
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="text-xs">General</TabsTrigger>
            <TabsTrigger value="academic" className="text-xs">Academic</TabsTrigger>
            <TabsTrigger value="finance" className="text-xs">Finance</TabsTrigger>
            <TabsTrigger value="examination" className="text-xs">Examination</TabsTrigger>
            <TabsTrigger value="qrcodes" className="text-xs">QR Codes</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Personal Details</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-muted-foreground">Gender:</span> {student.gender ? formatStatus(student.gender) : "—"}</div>
                  <div><span className="text-muted-foreground">Date of Birth:</span> {student.date_of_birth || "—"}</div>
                  <div><span className="text-muted-foreground">Email:</span> {student.email || "—"}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {student.phone || "—"}</div>
                  <div><span className="text-muted-foreground">NIDA:</span> {student.nida_number || "—"}</div>
                  <div><span className="text-muted-foreground">Registration Date:</span> {student.enrollment_date || "—"}</div>
                  <div><span className="text-muted-foreground">Form IV Index:</span> {student.form_iv_index || "—"}</div>
                  <div><span className="text-muted-foreground">NACTEVET Reg #:</span> {student.nactevet_reg_number || "—"}</div>
                  {student.address && <div className="col-span-2"><span className="text-muted-foreground">Address:</span> {student.address}</div>}
                </div>
              </CardContent>
            </Card>

            {/* Biometrics */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Fingerprint className="h-4 w-4" /> Biometrics</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center space-y-1">
                    <p className="text-xs text-muted-foreground">Passport Photo</p>
                    {photoUrl ? (
                      <img src={photoUrl} alt="Passport" className="h-24 w-[72px] rounded border object-cover mx-auto" />
                    ) : biometrics?.photo_path ? (
                      <div className="h-24 w-[72px] rounded border bg-destructive/10 text-destructive flex items-center justify-center text-[10px] mx-auto px-1 text-center">Failed to load</div>
                    ) : (
                      <div className="h-24 w-[72px] rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground mx-auto">N/A</div>
                    )}
                  </div>
                  <div className="text-center space-y-1">
                    <p className="text-xs text-muted-foreground">Signature</p>
                    {signatureUrl ? (
                      <img src={signatureUrl} alt="Signature" className="h-24 rounded border bg-background mx-auto object-contain" />
                    ) : biometrics?.signature_path ? (
                      <div className="h-24 rounded border bg-destructive/10 text-destructive flex items-center justify-center text-[10px] px-1 text-center">Failed to load</div>
                    ) : (
                      <div className="h-24 rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground">N/A</div>
                    )}
                  </div>
                  <div className="text-center space-y-1">
                    <p className="text-xs text-muted-foreground">Fingerprint</p>
                    {fingerprintUrl ? (
                      <img src={fingerprintUrl} alt="Fingerprint" className="h-24 w-24 rounded border object-cover mx-auto" />
                    ) : biometrics?.fingerprint_path ? (
                      <div className="h-24 w-24 rounded border bg-destructive/10 text-destructive flex items-center justify-center text-[10px] mx-auto px-1 text-center">Failed to load</div>
                    ) : (
                      <div className="h-24 w-24 rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground mx-auto">N/A</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Status History */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Status History</CardTitle></CardHeader>
              <CardContent>
                {statusHistory.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No status history</p>
                ) : (
                  <div className="relative pl-4 space-y-3">
                    <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
                    {statusHistory.map((entry: any, idx: number) => (
                      <div key={entry.id} className="relative flex items-start gap-3">
                        <div className={`absolute left-[-13px] top-1 h-2.5 w-2.5 rounded-full border-2 ${idx === statusHistory.length - 1 ? 'bg-primary border-primary' : 'bg-background border-muted-foreground/40'}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {entry.old_status && (
                              <>
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{formatStatus(entry.old_status)}</Badge>
                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              </>
                            )}
                            <Badge variant={idx === statusHistory.length - 1 ? "default" : "outline"} className="text-[10px] px-1.5 py-0">
                              {formatStatus(entry.new_status)}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {new Date(entry.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Academic Tab */}
          <TabsContent value="academic" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Academic Information</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-muted-foreground">Class:</span> {student.class || "—"}</div>
                  <div><span className="text-muted-foreground">Program:</span> {getProgram(student.class)}</div>
                  <div><span className="text-muted-foreground">NTA Level:</span> {student.nta_level || "—"}</div>
                  <div><span className="text-muted-foreground">Semester:</span> {student.current_semester || "—"}</div>
                  <div><span className="text-muted-foreground">Sit Status:</span> {student.sit_status ? formatStatus(student.sit_status) : "—"}</div>
                  <div><span className="text-muted-foreground">Admission:</span> {student.admission_status ? formatStatus(student.admission_status) : "—"}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Registered Courses</CardTitle></CardHeader>
              <CardContent>
                {registrations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No registrations</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Course</TableHead>
                        <TableHead>Semester</TableHead>
                        <TableHead>Year</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registrations.map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.courses?.course_code} - {r.courses?.course_name}</TableCell>
                          <TableCell>{r.semester}</TableCell>
                          <TableCell>{r.academic_year}</TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">Total Invoiced</p>
                  <p className="text-lg font-bold">{totalInvoiced.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                  <p className="text-lg font-bold text-primary">{totalPaid.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">Outstanding</p>
                  <p className={`text-lg font-bold ${outstanding > 0 ? "text-destructive" : "text-primary"}`}>{outstanding.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base">Financial Clearance</CardTitle></CardHeader>
              <CardContent>
                <Badge variant={student.financial_clearance ? "default" : "destructive"}>
                  {student.financial_clearance_status ? formatStatus(student.financial_clearance_status) : "Not Cleared"}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Invoices</CardTitle></CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No invoices</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv: any) => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.description || "—"}</TableCell>
                          <TableCell>{Number(inv.amount).toLocaleString()}</TableCell>
                          <TableCell>{inv.due_date || "—"}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Payments</CardTitle></CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No payments</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell>{Number(p.amount).toLocaleString()}</TableCell>
                          <TableCell>{p.payment_method || "—"}</TableCell>
                          <TableCell>{p.reference_number || "—"}</TableCell>
                          <TableCell>{p.payment_date || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Examination Tab */}
          <TabsContent value="examination" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Exam Results</CardTitle></CardHeader>
              <CardContent>
                {examResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No exam results</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Course</TableHead>
                        <TableHead>Semester</TableHead>
                        <TableHead>Marks</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {examResults.map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.courses?.course_code} - {r.courses?.course_name}</TableCell>
                          <TableCell>{r.semester}</TableCell>
                          <TableCell>{r.marks ?? "—"}</TableCell>
                          <TableCell>{r.grade || "—"}</TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Codes Tab */}
          <TabsContent value="qrcodes" className="space-y-4">
            <Card>
              <CardContent className="pt-4 space-y-4">
                {/* Visibility status per QR type */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    { label: "Administration", vis: qrVisAdmin, code: student.barcode_admin },
                    { label: "Academic", vis: qrVisAcademic, code: student.barcode_academic },
                    { label: "Examination", vis: qrVisExam, code: student.barcode_examination },
                    { label: "Accounting", vis: qrVisAccounting, code: student.barcode_accounting },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2 p-2 rounded border">
                      {item.vis.isVisible ? (
                        <Eye className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-xs">{item.label}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{item.vis.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator />

                <StudentBarcodes
                  studentName={`${student.first_name} ${student.last_name}`}
                  barcodeAdmin={qrVisAdmin.isVisible ? student.barcode_admin : null}
                  barcodeAcademic={qrVisAcademic.isVisible ? student.barcode_academic : null}
                  barcodeExamination={qrVisExam.isVisible ? student.barcode_examination : null}
                  barcodeAccounting={qrVisAccounting.isVisible ? student.barcode_accounting : null}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
